import { FieldTimeOutlined, LinkOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled, DisableIcon } from 'assets/images/svg';
import { CREDENTIAL_TYPE_PASSCODE_CARD, CREDENTIAL_TYPE_PASSCODE } from 'config/credentialTypes';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCredentialByID } from 'reduxStore/credentials/selectors';
import AssociateCardModal from 'zComponents/molecules/AssociateCardModal';
import { DeletePasscodeModal, UpdateCredentialModal } from 'zComponents/molecules';
import { Button, Divider, Dropdown, Flex } from 'zui/Antd';

const ManageCredentialDropdown = ({ credentialID, isTransparent = false }) => {
	const {
		token: {
			colorBgContainer,
			boxShadow,
			borderRadiusLG,
			paddingSM,
			fontSizeLG,
			colorPrimaryText,
			fontWeightStrong
		}
	} = theme.useToken();

	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const credentialTypeID = credential?.credentialTypeID;

	const canUpdateCredential = useManagePermittedRoles('updateCredential');

	const [openDropdown, setOpenDropdown] = useState(false);
	const [openUpdateCredentialModal, setOpenUpdateCredentialModal] = useState(false);

	const getItems = () => {
		const updateItem = {
			key: 'updateCredential',
			label: (
				<Button
					type='text'
					icon={<FieldTimeOutlined />}
					block
					disabled={!canUpdateCredential}
					style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
					onClick={() => {
						setOpenUpdateCredentialModal(true);
						setOpenDropdown(false);
					}}
				>
					Update Credential
				</Button>
			)
		};

		const disableItem = {
			key: 'disableCredential',
			label: (
				<Button
					type='text'
					disabled
					icon={
						<DisableIcon width={fontSizeLG} height={fontSizeLG} fillOpacity={0.45} color='currentColor' />
					}
					block
					style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
				>
					Disable Credential
				</Button>
			)
		};

		const deleteItem = {
			key: 'deleteCredential',
			label: (
				<Button
					danger
					type='text'
					icon={<DeletedFilled width={16} height={16} color='currentColor' />}
					block
					disabled
					style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
				>
					Delete Credential
				</Button>
			)
		};

		switch (credentialTypeID) {
			case CREDENTIAL_TYPE_PASSCODE_CARD:
				return [
					updateItem,
					...(!credential?.uniqueIdentifier
						? [
								{
									key: 'associateCard',
									label: (
										<AssociateCardModal credentialID={credentialID}>
											<Button
												type='text'
												icon={<LinkOutlined />}
												block
												disabled={!canUpdateCredential}
												style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
											>
												Associate Card
											</Button>
										</AssociateCardModal>
									)
								}
						  ]
						: []),
					{ key: 'divider', label: <Divider style={{ margin: 0 }} /> },
					disableItem,
					deleteItem
				];

			case CREDENTIAL_TYPE_PASSCODE:
				return [
					updateItem,
					{ key: 'divider', label: <Divider style={{ margin: 0 }} /> },
					disableItem,
					{
						key: 'deletePasscode',
						label: (
							<DeletePasscodeModal credentialID={credentialID}>
								<Button
									danger
									type='text'
									icon={<DeletedFilled width={16} height={16} color='currentColor' />}
									block
									disabled={!canUpdateCredential}
									style={{ justifyContent: 'flex-start', fontWeight: fontWeightStrong }}
								>
									Delete Passcode
								</Button>
							</DeletePasscodeModal>
						)
					}
				];

			default:
				return [
					updateItem,
					{ key: 'divider', label: <Divider style={{ margin: 0 }} /> },
					disableItem,
					deleteItem
				];
		}
	};

	const items = getItems();

	return (
		<>
			<Dropdown
				trigger={['click']}
				placement='bottomLeft'
				open={openDropdown}
				onOpenChange={(visible) => setOpenDropdown(visible)}
				dropdownRender={() => (
					<Flex
						style={{
							backgroundColor: colorBgContainer,
							borderRadius: borderRadiusLG,
							width: 240,
							boxShadow: boxShadow,
							padding: paddingSM
						}}
						vertical
						gap={paddingSM}
					>
						{items.map((item) => (
							<div key={item.key} onClick={() => item.key !== 'updateCredential' && setOpenDropdown(false)}>
								{item.label}
							</div>
						))}
					</Flex>
				)}
			>
				<Button type={isTransparent ? 'text' : 'primary'} style={{ color: isTransparent && colorPrimaryText }}>
					{isTransparent ? 'View' : 'Manage'} Credential
				</Button>
			</Dropdown>

			<UpdateCredentialModal
				open={openUpdateCredentialModal}
				setOpen={setOpenUpdateCredentialModal}
				credentialID={credentialID}
			>
				<div />
			</UpdateCredentialModal>
		</>
	);
};

export default ManageCredentialDropdown;
